import React, { useState, useEffect, useCallback } from 'react';
import marketplaceApi from '../marketplaceApi';

const DAY_TABS = [7, 14, 30, 90];
const METRICS = [
  { key: 'view',       label: 'Views',     icon: 'fa-eye',          color: 'info'    },
  { key: 'phone',      label: 'Phone',     icon: 'fa-phone',        color: 'primary' },
  { key: 'whatsapp',   label: 'WhatsApp',  icon: 'fa-whatsapp fab', color: 'success' },
  { key: 'share',      label: 'Shares',    icon: 'fa-share-alt',    color: 'warning' },
  { key: 'favorite',   label: 'Favorites', icon: 'fa-heart',        color: 'danger'  },
];

export default function MarketplaceAnalytics() {
  const [days,      setDays]      = useState(30);
  const [vehicles,  setVehicles]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await marketplaceApi.get('/clicks', { params: { days } });
      // Response: { success, data: [ { vehicle_id, vehicle_name, total, view, phone, whatsapp, ... } ] }
      setVehicles(data.data || []);
    } catch {
      setError('Failed to load analytics.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  // Totals across all vehicles
  const totals = vehicles.reduce((acc, v) => {
    METRICS.forEach(m => { acc[m.key] = (acc[m.key] || 0) + (v[m.key] || 0); });
    return acc;
  }, {});

  const maxTotal = vehicles.length > 0 ? Math.max(...vehicles.map(v => v.total || 0), 1) : 1;

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <h1 className="m-0"><i className="fas fa-chart-line mr-2 text-info" />Analytics</h1>
        </div>
      </div>
      <section className="content">
        <div className="container-fluid">

          {/* Day filter */}
          <ul className="nav nav-tabs mb-3">
            {DAY_TABS.map(d => (
              <li className="nav-item" key={d}>
                <button
                  className={`nav-link${days === d ? ' active' : ''}`}
                  onClick={() => setDays(d)}
                >
                  Last {d} days
                </button>
              </li>
            ))}
          </ul>

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : (
            <>
              {/* Stat cards */}
              <div className="row">
                {METRICS.map(({ key, label, icon, color }) => (
                  <div className="col-lg col-md-4 col-sm-6 mb-3" key={key}>
                    <div className={`small-box bg-${color}`}>
                      <div className="inner">
                        <h3>{totals[key] || 0}</h3>
                        <p>{label}</p>
                      </div>
                      <div className="icon"><i className={`fas ${icon}`} /></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Per-vehicle breakdown */}
              {vehicles.length > 0 ? (
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title"><i className="fas fa-motorcycle mr-2" />Per-Vehicle Performance</h3>
                  </div>
                  <div className="card-body">
                    {vehicles.map(v => {
                      const pct = Math.round(((v.total || 0) / maxTotal) * 100);
                      return (
                        <div key={v.vehicle_id} className="mb-4">
                          <div className="d-flex justify-content-between mb-1">
                            <strong>{v.vehicle_name || v.vehicle_id}</strong>
                            <small className="text-muted">{v.total || 0} total interactions</small>
                          </div>
                          <div className="progress progress-sm mb-1">
                            <div className="progress-bar bg-info" style={{ width: `${pct}%` }} />
                          </div>
                          <small className="text-muted">
                            {METRICS.map(m => `${m.label}: ${v[m.key] || 0}`).join(' · ')}
                          </small>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-chart-bar fa-2x mb-2 d-block" />
                  No analytics data yet for this period.
                </div>
              )}
            </>
          )}

        </div>
      </section>
    </div>
  );
}
